import React, { useEffect, useState } from 'react';
import { Row, Col, Card, CardBody, Modal } from 'reactstrap';
import { get, post, put, del } from 'helpers/api_helper';
import Datatables from 'pages/utils/table/datatable';
import { ROLES_URL } from 'helpers/url_helper';
import cookieHelper from 'helpers/getCookieData';
import { Link } from 'react-router-dom';
import { showSuccessAlert, showConfirmAlert } from "pages/utils/alertMessages";
import { AvForm, AvField } from 'availity-reactstrap-validation';
import { useTranslation } from 'react-i18next';

const UserRoleAccess = () => {
    const { t } = useTranslation();
    const clinic = cookieHelper.getCookie('_c') ? JSON.parse(atob(cookieHelper.getCookie('_c'))) : null;

    const [isForm, setIsForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [rows, setRows] = useState([]);
    const [permissionData, setPermissionData] = useState(null);
    const [selectedRoleName, setSelectedRoleName] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 10, Search: '' });

    const fetchData = async () => {
        setLoading(true);
        const { success, body } = await get(`${ROLES_URL}?page=${pagination.page}&limit=${pagination.limit}&search=${pagination.Search}`);
        if (success) {
            setRows(body);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (clinic) {
            setFormData(prev => ({
                ...prev,
                clinic_id: clinic.id
            }));
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [pagination]);

    const handleToggle = () => {
        setIsForm(!isForm);
        setEditMode(false);
        setEditingId(null);
        setFormData(clinic ? { clinic_id: clinic.id } : {});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async () => {
        const url = editMode ? `${ROLES_URL}/${editingId}` : ROLES_URL;
        const method = editMode ? put : post;

        const { success } = await method(url, formData);

        if (success) {
            showSuccessAlert(editMode ? t('Role updated successfully!') : t('Role created successfully!'));
            setIsForm(false);
            setFormData(clinic ? { clinic_id: clinic.id } : {});
            setEditMode(false);
            setEditingId(null);
            fetchData();
        }
    };

    const handleInvalidSubmit = () => {
        alert(t("Please enter all required fields."));
    };

    const handleEdit = (row) => {
        setFormData(row);
        setEditingId(row.role_id || row.id);
        setEditMode(true);
        setIsForm(true);
    };

    const handleDelete = async (id, sts) => {
        const action = sts === 1 ? t('De-activate') : t('Activate');

        const { isConfirmed } = await showConfirmAlert(`${action} ${t('Role')}`, `${t('Do you really want to')} ${action} ${t('this Role')}?`,
            `${t('Yes')}, ${action} ${t('it')}!`, `${action}d!`, `${t('Role has been updated successfully.')}`);

        if (isConfirmed) {
            const { success } = await del(`${ROLES_URL}/${id}?sts=${sts}`);
            if (success) {
                fetchData();
            }
        }
    };

    const handlePrevilizes = async (row) => {
        const roleId = row.role_id || row.id;
        setSelectedRoleName(row.name || 'Role');
        const { success, body } = await get(`permissions?isconfig=1&clinic_id=${clinic?.id}&role_id=${roleId}`);
        if (success && body?.modules) {
            const convertedArray = Object.entries(body.modules).map(([name, perms]) => ({
                name,
                ...perms,
            }));
            setPermissionData(convertedArray);
        } else {
            setPermissionData([]);
        }
    };

    const updatePermission = async (data) => {
        const obj = {
            client_id: clinic?.client_id || 1,
            role_id: editingId || 1,
            clinic_id: clinic?.id || 1,
            ...data
        };
        await post(`permissions/update`, obj);
    };

    const ManageRoleColumns = [
        { dataField: 'role_id', text: '#', formatter: (cell, row, rowIndex) => (pagination?.page - 1) * pagination?.limit + (rowIndex + 1) },
        { dataField: 'name', text: 'Role Name' },
        { dataField: 'description', text: 'Description' },
        {
            dataField: 'actions', text: 'Actions', isDummyField: true,
            headerStyle: { textAlign: 'center', width: '180px' },
            style: { textAlign: 'center' },
            formatter: (cell, row) => (
                <div className="table-actions-cell">
                    <button type="button" className="table-action-btn with-text btn-privilege" onClick={() => handlePrevilizes(row)} title={t("Set Module Privileges")}>
                        <i className="fas fa-key" />
                        <span>{t("Privileges")}</span>
                    </button>
                    <button type="button" className="table-action-btn icon-only btn-edit" onClick={() => handleEdit(row)} title={t("Edit Role")}>
                        <i className="fas fa-pencil-alt" />
                    </button>
                    <button type="button" className={`table-action-btn icon-only ${row.status === 0 ? 'btn-delete' : 'btn-restore'}`} onClick={() => handleDelete(row.role_id, row.status === 1 ? 0 : 1)} title={row.status === 0 ? t('Delete') : t('Deactivate')}>
                        <i className={`fas ${row.status === 0 ? 'fa-trash-alt' : 'fa-check'}`} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <React.Fragment>
            <Card>
                <CardBody>
                    <Datatables
                        isSearch={true}
                        columns={ManageRoleColumns}
                        showTableOnly={true}
                        rowsLength={rows?.totalItems || 0}
                        rows={rows.items || []}
                        keyField={'id'}
                        handleAddButton={handleToggle}
                        title={t("All User Roles & Access Control")}
                        isAdd={true}
                        isTableHead={true}
                        loading={loading}
                        ssr={setPagination}
                    />
                </CardBody>
            </Card>

            {/* Modal XL for Add / Edit Role */}
            <Modal isOpen={isForm} toggle={handleToggle} size="xl" centered>
                <AvForm onValidSubmit={handleSubmit} onInvalidSubmit={handleInvalidSubmit} model={formData}>
                    <div className="modal-header">
                        <h5 className="modal-title font-weight-bold">
                            {editMode ? t('Update Role') : t('Create New System Role')}
                        </h5>
                        <button type="button" className="btn-close" onClick={handleToggle} aria-label="Close" />
                    </div>
                    <div className="modal-body p-4">
                        <Row className="g-3">
                            <Col md={6}>
                                <div className="mb-2">
                                    <AvField
                                        name="name"
                                        label={t("Role Name *")}
                                        type="text"
                                        placeholder={t("e.g. Senior Dental Surgeon, Dental Hygienist, Front Desk")}
                                        value={formData.name || ''}
                                        onChange={handleChange}
                                        required
                                        errorMessage={t("Please enter Role Name")}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-2">
                                    <AvField
                                        name="description"
                                        label={t("Role Description *")}
                                        type="text"
                                        placeholder={t("e.g. Responsible for patient check-ins, appointments, and billing")}
                                        value={formData.description || ''}
                                        onChange={handleChange}
                                        required
                                        errorMessage={t("Please enter Role Description")}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-danger" onClick={handleToggle}>
                            {t("Cancel")}
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? t('Processing...') : (editMode ? t('Update Role') : t('Save Role'))}
                        </button>
                    </div>
                </AvForm>
            </Modal>

            {/* Modal XL for Module Privileges & Permissions */}
            <Modal isOpen={!!permissionData} toggle={() => setPermissionData(null)} size="xl" centered>
                <div className="modal-header">
                    <h5 className="modal-title font-weight-bold">
                        {t("Configure Permissions & Access Level")}: <span className="text-primary">{selectedRoleName}</span>
                    </h5>
                    <button type="button" className="btn-close" onClick={() => setPermissionData(null)} aria-label="Close" />
                </div>
                <div className="modal-body p-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: '35%' }}>{t("Feature / Module")}</th>
                                    <th className="text-center">{t("Accessible")}</th>
                                    <th className="text-center">{t("View (Read)")}</th>
                                    <th className="text-center">{t("Create")}</th>
                                    <th className="text-center">{t("Edit (Write)")}</th>
                                    <th className="text-center">{t("Delete")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(permissionData || []).map((item, i) => (
                                    <tr key={`perm_${i}`}>
                                        <td className="font-weight-bold text-slate-800 text-capitalize">
                                            {t(item.name?.replace(/_/g, ' ')) || item.name?.replace(/_/g, ' ')}
                                        </td>
                                        {["is_accessable", "is_readable", "is_creatable", "is_writable", "is_deletable"].map((field) => (
                                            <td key={`${field}_${i}`} className="text-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer' }}
                                                    checked={!!item[field]}
                                                    disabled={field !== "is_accessable" && !item.is_accessable}
                                                    onChange={async (e) => {
                                                        const updated = [...permissionData];
                                                        updated[i][field] = e.target.checked;

                                                        if (field !== 'is_accessable' && e.target.checked) {
                                                            updated[i].is_accessable = true;
                                                        }
                                                        if (field === 'is_accessable' && !e.target.checked) {
                                                            updated[i].is_readable = false;
                                                            updated[i].is_creatable = false;
                                                            updated[i].is_writable = false;
                                                            updated[i].is_deletable = false;
                                                        }

                                                        setPermissionData(updated);
                                                        await updatePermission(updated[i]);
                                                    }}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn btn-primary" onClick={() => setPermissionData(null)}>
                        {t("Done & Close")}
                    </button>
                </div>
            </Modal>
        </React.Fragment>
    );
};

export default UserRoleAccess;
